import {
  collection,
  getDocs,
  query,
  where,
  getCountFromServer,
  doc,
  deleteDoc,
} from 'firebase/firestore';
import {
  FirestoreCollection,
  InvitedUser,
  InvitedUserFields,
  AdminInvitedEmployees,
} from 'hubbl-shared';
import {firebaseFunctions, firestore} from '../firebase';
import {httpsCallable} from 'firebase/functions';

const getInvitedUsers = async (companyId: string): Promise<InvitedUser[]> => {
  const invitedUsersRef = collection(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.InvitedUsers,
  );

  const snapshot = await getDocs(invitedUsersRef);
  const invitedUsers = snapshot.docs.map(doc => ({
    ...doc.data(),
  })) as InvitedUser[];

  // Sort by status: pending first, rejected second, accepted last
  return invitedUsers.sort((a, b) => {
    if (a.status === 'accepted' && b.status !== 'accepted') return 1;
    if (a.status !== 'accepted' && b.status === 'accepted') return -1;
    if (a.status === 'pending' && b.status === 'rejected') return -1;
    if (a.status === 'rejected' && b.status === 'pending') return 1;
    return 0;
  });
};

const getPendingInvitedUsersCount = async (
  companyId: string,
): Promise<number> => {
  const invitedUsersRef = collection(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.InvitedUsers,
  );

  const pendingQuery = query(invitedUsersRef, where('status', '==', 'pending'));

  const snapshot = await getCountFromServer(pendingQuery);
  return snapshot.data().count;
};

const getPendingInvitedUsersForEvent = async (
  companyId: string,
  adminInvitedEmployees: AdminInvitedEmployees,
  adminUserId?: string,
): Promise<InvitedUser[]> => {
  const invitedUsersRef = collection(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.InvitedUsers,
  );

  let invitedUsersQuery;

  // Base query for pending users
  const baseQuery = query(invitedUsersRef, where('status', '==', 'pending'));

  // Apply additional filters based on AdminInvitedEmployees setting
  switch (adminInvitedEmployees) {
    case AdminInvitedEmployees.NONE:
      // Return empty array for NONE
      return [];
    case AdminInvitedEmployees.MY_INVITED_USERS:
      if (!adminUserId) {
        return [];
      }
      invitedUsersQuery = query(
        invitedUsersRef,
        where('status', '==', 'pending'),
        where('invitedByUserId', '==', adminUserId),
      );
      break;
    case AdminInvitedEmployees.ALL_INVITED_USERS:
      invitedUsersQuery = baseQuery;
      break;
    default:
      return [];
  }

  const snapshot = await getDocs(invitedUsersQuery);
  const invitedUsers = snapshot.docs.map(doc => ({
    ...doc.data(),
  })) as InvitedUser[];

  return invitedUsers;
};

interface OnInviteUsersByEmailParams {
  companyId: string;
  invitedUsers: InvitedUserFields[];
  customDescription?: string;
  sendInviteEmails?: boolean;
}

const onInviteUsersByEmail = async ({
  companyId,
  invitedUsers,
  customDescription,
  sendInviteEmails = true,
}: OnInviteUsersByEmailParams) => {
  const callableFunc = httpsCallable(firebaseFunctions, 'onInviteUsersByEmail');
  return callableFunc({
    companyId,
    invitedUsers,
    customDescription,
    sendInviteEmails,
  })
    .then(result => {
      console.log(result.data);
      const data = result.data;
      return data;
    })
    .catch(error => {
      console.log(`error: ${JSON.stringify(error)}`);
      throw error;
    });
};

const reinvitePendingUser = async (email: string) => {
  const callableFunc = httpsCallable(
    firebaseFunctions,
    'onReinvitePendingUser',
  );

  return callableFunc({email})
    .then(result => result.data)
    .catch(error => {
      console.log(`error: ${JSON.stringify(error)}`);
      throw error;
    });
};

const deleteInvitedUser = async (companyId: string, email: string) => {
  const invitedUserRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.InvitedUsers,
    email.toLowerCase().trim(),
  );
  await deleteDoc(invitedUserRef);
};

export const invitedUsers = {
  getInvitedUsers,
  getPendingInvitedUsersCount,
  onInviteUsersByEmail,
  getPendingInvitedUsersForEvent,
  reinvitePendingUser,
  deleteInvitedUser,
};
